import React from 'react'
import { EditableComponent } from '@adobe/aem-react-editable-components'
import {
  Image as UIFoundationsImage,
  Text as UIFoundationsText,
  TextSectionHtmlTags
} from '@dcxwam/dcx-wam-ui-foundations'
import {
  withErrorBoundary,
  ErrorFallback
} from '../../functional/ErrorBoundary'
import {
  getTextAlignmentMappedProp,
  getMarginBottomMappedProp,
  getImageMappedProps,
  ImageModel
} from '../../../utils/props-mappings'
import ImageEditConfig from './ImageEditConfig'
import { ImageModelProps } from './types'
import { EditableComponentPropsType } from '../../../utils/prop-types'
import withComponentRefresh from '../../../utils/aem/editor/withComponentRefresh'
import { useExtractEditableProps } from '../../../utils/aem/editor/extractEditableProps'
import { getFormattedSrcset } from '../../../utils/get-formatted-srcset'
import {
  WithAutomaticTestIdsType,
  withAutomaticTestIds
} from '../../../utils/aem/with-automatic-test-ids'

const Image = withComponentRefresh(
  withAutomaticTestIds(
    (
      props: WithAutomaticTestIdsType<
        EditableComponentPropsType<ImageModelProps>
      >
    ) => {
      const {
        image,
        alt,
        height: h,
        width: w,
        imageRenditions,
        ariaLabel,
        imageWidth,
        textAlign,
        srcset,
        marginBottom,
        id,
        dmImage,
        renditionWidths,
        renditionHeights,
        linkClass,
        ...rest
      } = props.model
      const { getDataTestIdObject } = props
      const editableProps = useExtractEditableProps(props, rest)

      const configSection = {
        component: TextSectionHtmlTags.P,
        hasMarginBottom: getMarginBottomMappedProp(marginBottom),
        textHorizontalAlignment: getTextAlignmentMappedProp(textAlign)
      }

      const srcSetData = getFormattedSrcset(srcset ?? '')
      const imageProps = {
        ...(props.model as ImageModel),
        srcSetData
      }
      const configImageAtom = {
        ...getImageMappedProps(imageProps),
        ...getDataTestIdObject('image', id)
      } as Parameters<typeof UIFoundationsImage>[0]

      return (
        <EditableComponent {...editableProps}>
          {image?.src ? (
            <UIFoundationsText.TextSection {...configSection}>
              <UIFoundationsImage {...configImageAtom} />
            </UIFoundationsText.TextSection>
          ) : (
            ImageEditConfig.emptyLabel
          )}
        </EditableComponent>
      )
    }
  )
)

const ImageWithErrorBoundary = withErrorBoundary(Image, {
  componentName: 'Image',
  fallback: <ErrorFallback componentName="Image" />
})

export default ImageWithErrorBoundary
